import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetShip } from "../../App/Context"
import { useEditState } from "../../App/useEditState"
import { StopUserAdvice } from "./StopAdvice"
import { BlockField, BlockFieldInputs, BlockRow } from "../../Components/Block"
import { EditGroup } from "../../Components/EditGroup"
import { UserAdviceDialog } from "../../Components/UserAdvice/UserAdvice"
import { OverlayViewDefinition } from "../OverlayView"
import { ILogStopType, IShipStatus, IStatus, IStatusType } from "../../Types"
import { logger, fieldNames } from "../../App/Logger"
import { UserAdviceViewsData } from "./Context"
import { UserAdviceStopViewProps } from "./Params"

export function UserAdviceStopView({ advice, state, statuses, fixedShipStatus }: UserAdviceStopViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const t = app.getT("UserAdviceStopView");
  const [edited, _onEdited, _resetEdited] = useEditState();
  const editableFields = ["endLocation", { name: "shipStatus", type: "int" }, { name: "statusId", type: "int" }, { name: "stopEngines", type: "bool" }, "stopType"];
  const initData = state.data || {};
  const [stopType, setStopType] = useState<ILogStopType>(initData.stopType || ILogStopType.endOfDay);
  const [shipStatus, setShipStatus] = useState<IShipStatus | undefined>(fixedShipStatus ?? initData.shipStatus);

  // Check if there are manual engine entries (no NMEA engine data) and engines are running
  const hasManualEnginesRunning = useMemo(() => {
    if (!ship || !statuses) {
      return false;
    }

    // Check if NMEA has engine data
    const hasNMEAEngineData = app.status?.nmea?.data?.engine && app.status.nmea.data.engine.length > 0;

    if (hasNMEAEngineData) {
      return false; // If NMEA has engine data, engines are not manual
    }

    // Check if there are any running engine statuses
    const engineStatuses = statuses.filter((s: IStatus) =>
      s.statusType === IStatusType.engine && s.endLogId === undefined,
    );

    return engineStatuses.length > 0;
  }, [ship, statuses, app.status]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const save = useCallback(async (_data: FormData, values: any) => {
    logger.debug("saving", fieldNames(values));

    state.setSelected(undefined);
    if (state.recordStop) {
      await state.recordStop(values);
    } else {
      await UserAdviceViewsData.stop(app.apiClient, values);
    }
  }, [hasManualEnginesRunning, ship, state, app]);

  const stopUserAdvice = useMemo(() => advice.parent as StopUserAdvice, [advice]);

  return (
    <UserAdviceDialog className="UserAdviceStopView">
      <EditGroup changed={edited} editableFields={editableFields} onSave={save}>
        {stopUserAdvice && stopUserAdvice.statusId && <input type="hidden" name="statusId" value={stopUserAdvice.statusId} />}
        {fixedShipStatus !== undefined && <input type="hidden" name="shipStatus" value={fixedShipStatus} />}
        <BlockRow className="block-row-infotext">
          {t(fixedShipStatus === undefined ? "text" : "text_fixed_status")}
        </BlockRow>
        <BlockRow>
          <BlockField className="block-row-checkboxes">
            <BlockFieldInputs className="block_field_small_24">
              <BlockField className="checkbox-text-bigger" label={t("end_of_day")} labelAfter={true} labelFor={"stopType-endOfDay"}>
                <input defaultChecked={stopType === ILogStopType.endOfDay} id={"stopType-endOfDay"} name="stopType" type="radio" className="" value={ILogStopType.endOfDay} onChange={() => setStopType(ILogStopType.endOfDay)} /></BlockField>
              <BlockField className="checkbox-text-bigger" label={t("stopover")} labelAfter={true} labelFor={"stopType-stopover"}>
                <input defaultChecked={stopType === ILogStopType.stopover} id={"stopType-stopover"} name="stopType" type="radio" className="" value={ILogStopType.stopover} onChange={() => setStopType(ILogStopType.stopover)} /></BlockField>
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>
        <BlockRow className="block-row-one-row">
          <BlockField label={t("end_location")}><input name="endLocation" className="block_field_small_30" defaultValue={initData.endLocation || ""} /></BlockField>
        </BlockRow>

        {fixedShipStatus === undefined && (
          <BlockRow>
            <BlockField className="block-row-checkboxes">
              {[IShipStatus.anchor, IShipStatus.buoy, IShipStatus.port, IShipStatus.drifting].map((status) => {
                return (
                  <BlockField label={t(`status_${IShipStatus[status]}`)} labelAfter={true} labelFor={`shipStatus-${status}`} key={status}>
                    <input defaultChecked={status === initData.shipStatus} id={`shipStatus-${status}`} name="shipStatus" type="radio" className="" value={status} onChange={() => setShipStatus(status)} />
                  </BlockField>
                );
              })}
            </BlockField>
            {stopType == ILogStopType.stopover && (
              <BlockField className="block-row-checkboxes">
                {[IShipStatus.lock, IShipStatus.bunkering, IShipStatus.bridge, IShipStatus.waiting].map((status) => {
                  return (
                    <BlockField label={t(`status_${IShipStatus[status]}`)} labelAfter={true} labelFor={`shipStatus-${status}`} key={status}>
                      <input defaultChecked={status === initData.shipStatus} id={`shipStatus-${status}`} name="shipStatus" type="radio" className="" value={status} onChange={() => setShipStatus(status)} />
                    </BlockField>
                  );
                })}
              </BlockField>
            )}
          </BlockRow>
        )}

        {hasManualEnginesRunning && (
          <>
            <BlockRow className="block-row-top20 text-bigger">
              <strong>{t("manual_engine_entries")}</strong>
            </BlockRow>
            <BlockRow className="block-row-infotext">
              {t("manual_engine_entries_text")}
            </BlockRow>
            <BlockRow className="block-row-checkboxes">
              <BlockField label={t("stop_engines")} labelAfter={true} labelFor="stopEngines">
                <input id="stopEngines" name="stopEngines" value="true" type="checkbox" />
              </BlockField>
            </BlockRow>
          </>
        )}

        <BlockRow actions={true} className="bottom">
          <BlockField><button className="button" onClick={() => state.setSelected(undefined)}>{t("cancel")}</button></BlockField>
          <BlockField><button className="button default" type="submit" disabled={shipStatus === undefined}>{t("save")}</button></BlockField>
        </BlockRow>
      </EditGroup>
    </UserAdviceDialog>
  );
}

// This dialog's own overlay attributes (#1171), declared here rather than in
// `UserAdviceOverlayView` so the dialog keeps deciding how it presents itself. It
// takes its title straight from the advice whose button opened it, and stays
// closable unless that advice asked otherwise. Deliberately hook-free -- one of
// these is called per open advice, so a hook would vary React's hook count.
export function userAdviceStopOverlay(props: UserAdviceStopViewProps): OverlayViewDefinition {
  return {
    close: !props.advice.props.hideClose,
    title: props.advice.props.title,
    render: () => <UserAdviceStopView {...props} />,
  };
}
